import type { SubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';

/**
 * Échec de « Garder Unboared » : `reread` = un autre onglet est en train de le faire (relire le
 * statut, pas d'erreur) ; sinon la clé du message. Doublon, planificateur, pause : réessayer ou
 * « Renouveler » ne servirait à rien (ou ferait payer deux fois) → contact.
 */
export function keepFailure(error: unknown): { reread: boolean; messageKey: string } {
    // `SubscriptionActionError.code` (lu sans importer le service : ce module reste pur, testable).
    const code = typeof (error as { code?: unknown })?.code === 'string' ? (error as { code: string }).code : '';
    if (code === 'operation_in_progress') return { reread: true, messageKey: '' };
    const manual = ['multiple_subscriptions', 'has_schedule', 'already_paused', 'not_active'].includes(code);
    return { reread: false, messageKey: manual ? 'account.keep.errorContact' : 'account.keep.error' };
}

/**
 * Arrêt programmé : en fin de période (`cancelAtPeriodEnd`) OU à une date
 * (`cancelAt` seul, facturation flexible : le portail ne pose que celle-ci).
 */
export function isCancelScheduled(sub: Pick<SubscriptionStatus, 'cancelAtPeriodEnd' | 'cancelAt'> | null): boolean {
    return !!sub && (sub.cancelAtPeriodEnd || !!sub.cancelAt);
}

/**
 * « Garder Unboared » proposable : arrêt programmé sur un essai ou un
 * abonnement payant, hors pause (la pause gère déjà la résiliation) et hors
 * impayé (le geste utile est alors de mettre la carte à jour).
 */
export function canKeep(sub: SubscriptionStatus | null): sub is SubscriptionStatus {
    return (
        !!sub &&
        isCancelScheduled(sub) &&
        ['trialing', 'active'].includes(sub.status) &&
        !sub.pause &&
        // Planificateur Stripe : le serveur refuserait (cas géré à la main).
        sub.pauseReason !== 'has_schedule'
    );
}

/** Fin d'accès affichée : la plus proche entre l'arrêt programmé et la fin d'essai / de période. */
export function keepEndAt(sub: SubscriptionStatus): number | null {
    const periodEnd = (sub.status === 'trialing' ? sub.trialEnd : sub.currentPeriodEnd) || sub.currentPeriodEnd;
    const dates = [sub.cancelAt, periodEnd].filter((d): d is number => typeof d === 'number' && d > 0);
    return dates.length ? Math.min(...dates) : null;
}

/**
 * `?client=` d'un lien d'email : identifiant de document Firestore plausible, sinon ignoré. Les
 * identifiants de lieu peuvent contenir des accents, une apostrophe (droite ou typographique), `&`
 * ou `.` (`autoentreprise-sébastien-dubuisson`, `sasu-clevy’s-festive-pub`). Refusés : `/`, espaces,
 * caractères de contrôle, de format (invisibles) et demi-paires UTF-16 (`\p{Cc}\p{Cf}\p{Cs}`),
 * `.`/`..`, noms réservés `__…__`, plus de 128 caractères. MÊME règle que `isValidClientId` de
 * stripe-b2b (`clientAuth.js`) : les garder identiques.
 */
const CLIENT_ID_FORBIDDEN = /[/\s\p{Cc}\p{Cf}\p{Cs}]/u;
export function linkClientId(raw: string | null): string {
    if (!raw || raw.length > 128 || raw === '.' || raw === '..' || /^__.*__$/.test(raw)) return '';
    return CLIENT_ID_FORBIDDEN.test(raw) ? '' : raw;
}

/**
 * Lieu affiché par une page ouverte depuis un lien d'email (`/account/keep`, `/account/annual`).
 * - navigation interne (`state.clientID`) : prioritaire ;
 * - `?client=` valide : ce lieu (l'accès est ensuite vérifié par la lecture Firestore) ;
 * - `?client=` PRÉSENT mais invalide : AUCUN repli sur le lieu mémorisé de l'appareil (un admin de
 *   plusieurs lieux agirait sur le mauvais) → `invalidLink` ;
 * - pas de `?client=` : lieu mémorisé.
 */
export function resolveLinkClient(args: {
    linkFocus: boolean;
    raw: string | null;
    stateId?: string | null;
    storedId: string;
}): {
    clientID: string;
    invalidLink: boolean;
} {
    if (args.stateId) return { clientID: args.stateId, invalidLink: false };
    if (!args.linkFocus) return { clientID: args.storedId, invalidLink: false };
    if (args.raw === null) return { clientID: args.storedId, invalidLink: false };
    const id = linkClientId(args.raw);
    return id ? { clientID: id, invalidLink: false } : { clientID: '', invalidLink: true };
}
